/**
 * StatusBadge — accessible pill badge showing a run/step status.
 *
 * Renders a Lucide icon + colour-coded label. Uses CSS custom properties from
 * tokens.css so each variant picks up the right colour automatically.
 *
 * Supports all 8 status values defined in the API contract.
 */

import type { ComponentType } from 'react'
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  Circle,
  Ban,
  TimerOff,
  ArrowRight,
  HelpCircle,
  type LucideProps,
} from 'lucide-react'
import type { RunStatus, StepStatus } from '../../api/types'

type Status = RunStatus | StepStatus

interface StatusConfig {
  label: string
  Icon: ComponentType<LucideProps>
  variant: string
  spin?: boolean
}

const STATUS_MAP: Record<string, StatusConfig> = {
  queued:               { label: 'Queued',            Icon: Circle,         variant: 'queued'    },
  running:              { label: 'Running',           Icon: Loader2,        variant: 'running', spin: true },
  succeeded:            { label: 'Succeeded',         Icon: CheckCircle2,   variant: 'succeeded' },
  completed:            { label: 'Completed',         Icon: CheckCircle2,   variant: 'succeeded' },
  failed:               { label: 'Failed',            Icon: XCircle,        variant: 'failed'    },
  cancelled:            { label: 'Cancelled',         Icon: Ban,            variant: 'cancelled' },
  timed_out:            { label: 'Timed out',         Icon: TimerOff,       variant: 'timed_out' },
  skipped:              { label: 'Skipped',           Icon: ArrowRight,     variant: 'skipped'   },
  admitted:             { label: 'Admitted',           Icon: Circle,         variant: 'queued'    },
  waiting:              { label: 'Waiting',            Icon: Clock,          variant: 'approval'  },
  waiting_for_approval: { label: 'Awaiting approval',  Icon: Clock,          variant: 'approval'  },
}

const FALLBACK: StatusConfig = {
  label: 'Unknown',
  Icon: HelpCircle,
  variant: 'unknown',
}

const VARIANT_CLASSES: Record<string, string> = {
  queued:    'bg-[rgba(148,163,184,0.12)] text-[#8fa3bd] border-[rgba(148,163,184,0.18)]',
  running:   'bg-[rgba(6,182,212,0.14)] text-[#06b6d4] border-[rgba(6,182,212,0.22)] [animation:badge-pulse_2s_ease-in-out_infinite]',
  succeeded: 'bg-[rgba(34,197,94,0.14)] text-[#22c55e] border-[rgba(34,197,94,0.22)]',
  failed:    'bg-[rgba(239,68,68,0.14)] text-[#ef4444] border-[rgba(239,68,68,0.22)]',
  cancelled: 'bg-[rgba(148,163,184,0.08)] text-[#4a6280] border-[rgba(148,163,184,0.12)]',
  timed_out: 'bg-[rgba(245,158,11,0.14)] text-[#f59e0b] border-[rgba(245,158,11,0.22)]',
  skipped:   'bg-[rgba(148,163,184,0.08)] text-[#4a6280] border-[rgba(148,163,184,0.12)]',
  approval:  'bg-[rgba(168,85,247,0.14)] text-[#a855f7] border-[rgba(168,85,247,0.22)] [animation:badge-pulse_2.4s_ease-in-out_infinite]',
  unknown:   'bg-[rgba(148,163,184,0.08)] text-[#4a6280] border-[rgba(148,163,184,0.10)]',
}

interface StatusBadgeProps {
  status: Status | string
  /** If true, only the icon is shown (for compact table cells). */
  iconOnly?: boolean
  className?: string
}

export function StatusBadge({ status, iconOnly = false, className = '' }: StatusBadgeProps) {
  const cfg = STATUS_MAP[status] ?? { ...FALLBACK, label: status }
  const { Icon, spin } = cfg

  return (
    <span
      className={`inline-flex items-center gap-[5px] px-2 py-[2px] rounded-full text-[11px] font-semibold tracking-[0.02em] whitespace-nowrap border ${VARIANT_CLASSES[cfg.variant] ?? VARIANT_CLASSES.unknown} ${className}`.trim()}
      role="img"
      aria-label={cfg.label}
      title={cfg.label}
    >
      <span aria-hidden="true">
        <Icon size={12} className={spin ? 'animate-spin' : undefined} />
      </span>
      {!iconOnly && (
        <span>{cfg.label}</span>
      )}
    </span>
  )
}
